import React, { useState, useEffect, useRef } from "react";
import apiClient, { getApiError } from "../apiClient";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useShop } from "../context/ShopContext";
import { FaSpinner } from "react-icons/fa";
import EmptyState from "./EmptyState";
import "./parchaTable.css";
import { fetchSortOrderMap, applySortOrder, buildCategorySegments } from "../utils/sortOrder";

/* ─── Brand colours ──────────────────────────── */
const P   = "#6D214F";
const PDK = "#4A1436";
const GRN = "#16A34A";
const ORG = "#EA580C";
const BDR = "#E5E7EB";
const MUT = "#6B7280";
const fmt = n => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const pad      = n => String(n).padStart(2, "0");
const nowD     = new Date();
const timeStr  = nowD.toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" });
const todayISO = `${nowD.getFullYear()}-${pad(nowD.getMonth()+1)}-${pad(nowD.getDate())}`;

/* ─── Category emoji map ──────────────────────── */
const CAT_EMOJI_MAP = {
  WHISKY:"🥃", WHISKEY:"🥃", WINE:"🍷", BEER:"🍺", RUM:"🥃",
  VODKA:"🍸", GIN:"🍹", BRANDY:"🥃", LIQUEUR:"🍾", CHAMPAGNE:"🥂",
  देशी:"🏺", अंग्रेजी:"🥂",
};
const catEmoji = c => CAT_EMOJI_MAP[c?.toUpperCase()] || "🍾";

/* ═══════════════════════════════════════════════════
   अंग्रेजी पर्चा (Back-date) — typeId 1
   Uses GetStockProductDataWithDate + SaveProductDataWithDate
═══════════════════════════════════════════════════ */
export default function Stock() {
  const navigate = useNavigate();
  const { userId, companyId } = useShop();

  const [shopId,          setShopId]          = useState(null);
  const [tokenString,     setToken]           = useState(null);
  const [shopName,        setShopName]        = useState(null);
  const [apiConditionMet, setApiConditionMet] = useState(null);

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  const [date,      setDate]      = useState(localStorage.getItem("sip_date_eng_parcha") || todayISO);
  const [search,    setSearch]    = useState("");
  const [activeCat, setActiveCat] = useState("ALL");
  const [preview,   setPreview]   = useState(false);

  const [selectedVars, setSelectedVars] = useState({});
  const closingRefs = useRef({});
  const [popupData1,   setPopupData1]   = useState([]);
  const [popupData2,   setPopupData2]   = useState([]);


  /* ── Daily Closing Summary (local UI state only) ── */
  const [commission, setCommission] = useState("");
  const [phonePe,    setPhonePe]    = useState("");
  const [todayExp,   setTodayExp]   = useState("");
  const [expNote,    setExpNote]    = useState("");
  const [addNotes,   setAddNotes]   = useState("");

  const showToast = (msg, type = "error") => {
    toast.dismiss();
    type === "success" ? toast.success(msg) : toast.error(msg);
  };

  /* ── Auth ── */
  useEffect(() => {
    const sid   = localStorage.getItem("shopId");
    const token = localStorage.getItem("tokenString");
    const sName = localStorage.getItem("shopName");
    if (sid && token && sName) {
      setShopId(sid); setToken(token); setShopName(sName);
    } else {
      showToast("Please log in first!", "error");
      navigate("/signin");
    }
  }, [navigate]);

  /* ── Check if stock already submitted for selected date ── */
  const checkStockData = async (d = date) => {
    try {
      const res = await apiClient.post(
        "/Liquor/GetCheckStock",
        { shopId, typeId: "1", stockDate: d || todayISO }
      );
      setApiConditionMet(res.data?.data === true ? false : true);
    } catch { setApiConditionMet(true); }
  };
  useEffect(() => { if (shopId) checkStockData(date); }, [shopId, date]);

  /* ── Warn user before closing/refreshing with unsaved entries ── */
  useEffect(() => {
    const hasUnsaved = rows.some(r => r.closing !== "");
    const handler = e => { if (hasUnsaved) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [rows]);

  /* ── Fetch stock for selected date ── */
  const fetchStock = async () => {
    if (!shopId || !tokenString) return;
    setLoading(true);
    try {
      const res = await apiClient.post(
        "/Liquor/GetStockProductDataWithDate",
        { shopId, typeId: "1", stockDate: date || todayISO }
      );
      const data = res.data.data || [];
      const raw = data.map(p => ({
        id:           p.id,
        cat:          p.catName || p.category || p.categoryName || p.typeName || "अंग्रेजी",
        pName:        p.pName,
        packing:      p.packing,
        variantType:  (p.variantType || "").replace(/[^a-zA-Z0-9\s]/g, "").trim() || (/\bpet\b/i.test(p.packing || "") ? "pet" : "Bottle"),
        mrp:          parseFloat(p.mrp)          || 0,
        openingStock: parseFloat(p.openingStock) || 0,
        receivedQty:  parseFloat(p.receivedQty)  || 0,
        transferQty:  parseFloat(p.transferQty)  || 0,
        closing:      "",
      }));
      // Deduplicate by id — SP joins can return the same variant row more than once
      const _seenIds = new Set();
      const deduped = raw.filter(r => _seenIds.has(r.id) ? false : _seenIds.add(r.id));
      // GetStockProductDataWithDate doesn't guarantee global-sort-order sequencing, so
      // re-sort by the authoritative shop+type sort-order map before rendering.
      const sortMap = await fetchSortOrderMap(shopId, "1");
      const mapped = applySortOrder(deduped, sortMap);
      /* ── Restore draft if user had unsaved entries ── */
      const draftKey = `sipstocks_draft_en_${shopId}_${date}`;
      const savedDraft = localStorage.getItem(draftKey);
      let finalRows = mapped;
      if (savedDraft) {
        try {
          const saved = JSON.parse(savedDraft);
          const restored = mapped.map(r => {
            const d = saved.find(s => s.id === r.id);
            return d ? { ...r, closing: d.closing } : r;
          });
          if (restored.some(r => r.closing !== "")) {
            finalRows = restored;
            showToast("Draft restored — unsaved entries recovered", "success");
          }
        } catch { /* ignore corrupt draft */ }
      }
      setRows(finalRows);

      const initSel = {};
      const seen = new Set();
      mapped.forEach(r => {
        if (!seen.has(r.pName)) { seen.add(r.pName); initSel[r.pName] = r.id; }
      });
      setSelectedVars(initSel);
    } catch (err) {
      showToast("Error fetching stock data", "error");
      console.error(err);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (shopId && tokenString && shopName) fetchStock();
  }, [shopId, tokenString, shopName, date]);

  /* ── Closing update — also saves draft synchronously on every change ── */
  const handleClosing = (id, val) => {
    const newRows = rows.map(r => {
      if (r.id !== id) return r;
      if (val === "") return { ...r, closing: "" };
      const n = parseFloat(val);
      if (isNaN(n) || n < 0) return r;
      const max = r.openingStock + r.receivedQty - r.transferQty;
      if (n > max) { showToast(`Closing cannot exceed ${max}`, "error"); return r; }
      return { ...r, closing: val };
    });
    setRows(newRows);
    // Persist draft immediately — do not wait for useEffect scheduling
    if (shopId && date) {
      const filled = newRows.filter(r => r.closing !== "");
      if (filled.length > 0)
        localStorage.setItem(
          `sipstocks_draft_en_${shopId}_${date}`,
          JSON.stringify(filled.map(r => ({ id: r.id, closing: r.closing })))
        );
    }
  };

  /* ── Transaction detail modal ── */
  const fetchTransactionData = async (rowId, type) => {
    try {
      const res = await apiClient.post(
        "/Liquor/GetDailyTransaction",
        { shopId, stockDate: date || todayISO }
      );
      if (res.data?.code === 200) {
        if (type === "received")
          setPopupData1(res.data.data?.trnReceived?.filter(i => i.productId === rowId) || []);
        else
          setPopupData2(res.data.data?.trnTransfer?.filter(i => i.productId === rowId) || []);
      }
    } catch (err) { console.error(err); }
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    const stocks = rows
      .filter(r => r.closing !== "")
      .map(r => {
        const cl  = parseFloat(r.closing);
        const tot = r.openingStock + r.receivedQty - r.transferQty;
        const sl  = Math.max(0, tot - cl);
        return {
          productId:     r.id,
          categoryName:  r.cat,
          receivedStock: r.receivedQty,
          transferStock: r.transferQty,
          currentStock:  cl,
          soldStock:     sl,
          totalAmount:   sl * r.mrp,
          shopId,
          stockDate: date || todayISO,
        };
      });
    if (!stocks.length) {
      showToast("Please enter closing stock for at least one product.", "error"); return;
    }
    const txnDate = date || todayISO;
    const transactions = [
      ...(parseFloat(phonePe) > 0 ? [{ typeId:1, shopId, amount:parseFloat(phonePe), date:txnDate, name:"", mode:"ONLINE" }] : []),
    ];
    const expenses = [
      ...(parseFloat(commission) > 0 ? [{ shopId, expenseId: 13, expenseAmount: parseFloat(commission), userId, remark: "English Commission", expenseDate: txnDate }] : []),
      ...(parseFloat(todayExp)  > 0 ? [{ shopId, expenseId: 77, expenseAmount: parseFloat(todayExp),  userId, remark: expNote || "Daily Expense", expenseDate: txnDate }] : []),
    ];
    try {
      setLoading(true);
      const res = await apiClient.post("/Liquor/SaveStockComplete", { stocks, transactions, expenses });
      if (res.status === 200) {
        if (res.data?.data?.includes("already exist")) {
          showToast(res.data.data, "error"); return;
        }
        if (res.data?.data?.includes("Negative stock")) {
          showToast("Negative stock detected!", "error"); return;
        }
        showToast("Stock report submitted successfully!", "success");
        localStorage.removeItem(`sipstocks_draft_en_${shopId}_${date}`);
        await checkStockData(date); fetchStock();
      }
    } catch (err) { showToast(getApiError(err, "Error submitting stock report!"), "error"); }
    finally   { setLoading(false); }
  };

  /* ── Helpers ── */
  const rowTotal = r => r.openingStock + r.receivedQty - r.transferQty;
  const rowSold  = r => { const cl = parseFloat(r.closing); return isNaN(cl) ? 0 : Math.max(0, rowTotal(r) - cl); };
  const rowValue = r => rowSold(r) * r.mrp;

  const filtered = rows.filter(r => {
    const matchCat  = activeCat === "ALL" || r.cat === activeCat;
    const matchSrch = r.pName.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSrch;
  });

  const uniqueCats  = [...new Set(rows.map(r => r.cat).filter(Boolean))];
  const filteredSegments = buildCategorySegments(filtered);

  const totalSold   = rows.reduce((s, r) => s + rowSold(r),  0);
  const totalValue  = rows.reduce((s, r) => s + rowValue(r), 0);
  const cashColl    = Math.max(0, totalValue - (parseFloat(commission)||0) - (parseFloat(phonePe)||0));
  const filledCount = rows.filter(r => r.closing !== "").length;
  const displayDate = new Date(date || todayISO).toLocaleDateString("en-GB");

  const mlSize = p => { const m = (p||"").match(/\d+(?:\.\d+)?/); return m ? parseFloat(m[0]) : 0; };

  const buildGroups = catRows => {
    const order = []; const map = new Map();
    catRows.forEach(r => {
      if (!map.has(r.pName)) { map.set(r.pName, []); order.push(r.pName); }
      map.get(r.pName).push(r);
    });
    return order.map(name => ({
      name,
      variants: (() => { const seen = new Set(); return map.get(name).filter(v => { const k = v.packing.toLowerCase(); return seen.has(k) ? false : seen.add(k); }).sort((a, b) => mlSize(b.packing) - mlSize(a.packing)); })(),
    }));
  };

  const selectVariant = (pName, id) =>
    setSelectedVars(prev => ({ ...prev, [pName]: id }));

  const navigatePills = (fromId, dir) => {
    const pills = Array.from(document.querySelectorAll('.parcha-var-pill[data-vid]'));
    const idx = pills.findIndex(p => p.dataset.vid === String(fromId));
    const target = pills[idx + dir];
    if (target) target.focus();
  };
  const focusClosingInput = (variantId) => {
    const inp = closingRefs.current[variantId];
    if (inp) { inp.focus(); inp.select(); }
  };

  /* ── Reset all closing entries ── */
  const handleReset = () => {
    if (shopId && date) localStorage.removeItem(`sipstocks_draft_en_${shopId}_${date}`);
    setRows(rows.map(r => ({ ...r, closing: "" })));
    showToast("All entries cleared", "success");
  };

  /* ═══════════════════════════════════════════ */
  return (
    <div className="parcha-page">
      <ToastContainer />
      <style>{`
        .parcha-var-pill:focus {
          outline: 2px solid #6D214F;
          outline-offset: 2px;
          box-shadow: 0 0 0 3px rgba(109,33,79,0.18);
          z-index: 1;
          position: relative;
        }
        .parcha-var-pill[data-vid]:focus {
          transform: scale(1.05);
          transition: transform 0.1s;
        }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }

        /* ── Mobile v2 card layout ── */
        .m2cat-hdr{display:flex;justify-content:space-between;align-items:center;
          padding:10px 14px;background:linear-gradient(90deg,#6D214F,#9B3D6F);
          border-radius:10px;margin:0 0 8px;}
        .m2cat-name{font-size:12px;font-weight:800;color:#fff;letter-spacing:1px;text-transform:uppercase;}
        .m2cat-count{font-size:10px;color:rgba(255,255,255,0.75);font-weight:600;}

        .m2card{background:#fff;border-radius:10px;margin:0 0 12px;
          box-shadow:0 1px 8px rgba(0,0,0,0.09);overflow:hidden;}
        .m2card-hdr{display:flex;justify-content:space-between;align-items:flex-start;
          padding:11px 14px 6px;}
        .m2card-name{font-size:12px;font-weight:800;color:#111827;line-height:1.35;flex:1;margin-right:8px;}
        .m2card-filled{font-size:11px;font-weight:700;flex-shrink:0;padding-top:1px;}

        .m2sect{font-size:9px;font-weight:700;color:#6D214F;text-align:center;
          letter-spacing:1.5px;padding:4px 0;border-top:1px solid #F3F4F6;
          border-bottom:1px solid #F3F4F6;background:#FAFAFA;}
        .m2sect.pet{color:#10B981;}

        .m2tbl{width:100%;border-collapse:collapse;font-size:11px;}
        .m2tbl thead tr{background:#F9F9F9;}
        .m2tbl thead th{padding:5px 4px;font-size:9px;font-weight:700;color:#9CA3AF;
          text-align:center;letter-spacing:0.4px;border-bottom:1px solid #F0F0F0;}
        .m2th-l{text-align:left!important;padding-left:14px!important;}
        .m2th-r{padding-right:12px!important;}
        .m2th-recv{color:#16A34A!important;}
        .m2th-trnf{color:#EA580C!important;}
        .m2tbl tbody tr{border-bottom:1px solid #F9F9F9;}
        .m2tr-done{background:#FDF9FB;}
        .m2td-var{padding:8px 4px 8px 14px;font-weight:700;color:#111827;white-space:nowrap;font-size:12px;}
        .m2td-c{padding:8px 4px;text-align:center;color:#374151;}
        .m2td-recv{padding:8px 4px;text-align:center;font-weight:700;color:#16A34A;font-size:12px;}
        .m2td-trnf{padding:8px 4px;text-align:center;font-weight:700;color:#EA580C;font-size:12px;}
        .m2dash{color:#D1D5DB;}
        .m2td-close{padding:5px 4px;text-align:center;}
        .m2inp{width:50px;text-align:center;border:1.5px solid #E5E7EB;border-radius:6px;
          padding:5px 2px;font-size:13px;font-weight:700;color:#374151;
          background:#FAFAFA;-moz-appearance:textfield;}
        .m2inp:focus{outline:none;border-color:#6D214F;background:#fff;
          box-shadow:0 0 0 2px rgba(109,33,79,0.15);}
        .m2inp.done{border-color:#6D214F55;background:#FDF4F8;color:#6D214F;}
        .m2inp::-webkit-outer-spin-button,.m2inp::-webkit-inner-spin-button{-webkit-appearance:none;}
        .m2td-val{padding:8px 12px 8px 4px;text-align:right;font-size:11px;color:#D1D5DB;font-weight:600;}
        .m2td-val.pos{color:#111827;font-weight:700;}

        .m2card-footer{display:flex;justify-content:space-between;align-items:center;
          padding:9px 14px;border-top:2px solid #F3E9F0;background:#FDF4F8;
          font-size:9px;font-weight:700;color:#9CA3AF;letter-spacing:0.5px;text-transform:uppercase;}
        .m2footer-val{font-size:15px;font-weight:800;color:#9CA3AF;}
        .m2footer-val.pos{color:#6D214F;}
      `}</style>

      {/* PAGE HEADER */}
      <div className="parcha-header">
        <h1 className="parcha-title">अंग्रेजी पर्चा</h1>
        <p className="parcha-subtitle">Digital stock register for liquor management</p>
        <div className="parcha-meta">
          <span>Date: {displayDate}</span>
          <span>Shop: {shopName} &nbsp;|&nbsp; Time: {timeStr}</span>
        </div>
        <hr className="parcha-divider"/>
      </div>

      {/* FILTER CARD */}
      <div className="parcha-filter-card-wrap">
        <div className="parcha-filter-card">
          <div className="parcha-filter-field" style={{flex:"1 1 220px"}}>
            <div className="parcha-filter-label">Search Products</div>
            <div className="parcha-filter-inpwrap">
              <span className="parcha-filter-ico">🔍</span>
              <input type="text" placeholder="Search by product name or variant…"
                value={search} onChange={e=>setSearch(e.target.value)}
                className="parcha-filter-inp ico"/>
            </div>
          </div>
          <div className="parcha-filter-field" style={{flex:"0 0 190px"}}>
            <div className="parcha-filter-label">Select Date</div>
            <div className="parcha-filter-inpwrap">
              <span className="parcha-filter-ico">📅</span>
              <input type="date" value={date} max={todayISO}
                onChange={e=>{setDate(e.target.value);localStorage.setItem("sip_date_eng_parcha",e.target.value);setActiveCat("ALL");setSearch("");setRows([]);setApiConditionMet(null);}}
                className="parcha-filter-inp ico"/>
            </div>
          </div>
          <div className="parcha-filter-field" style={{flex:"0 0 190px"}}>
            <div className="parcha-filter-label">Category</div>
            <div className="parcha-filter-inpwrap">
              <span className="parcha-filter-ico" style={{fontSize:11,top:"50%",transform:"translateY(-50%)"}}>▼</span>
              <select value={activeCat} onChange={e=>setActiveCat(e.target.value)}
                className="parcha-filter-sel">
                <option value="ALL">All Categories</option>
                {uniqueCats.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="parcha-filter-field" style={{flex:"0 0 auto",alignSelf:"flex-end"}}>
            <button onClick={handleReset}
              style={{height:40,padding:"0 18px",border:"1.5px solid #E5E7EB",borderRadius:8,
                background:"#fff",color:"#374151",fontSize:13,fontWeight:600,cursor:"pointer",
                display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
              🔄 Reset
            </button>
          </div>
        </div>
      </div>
      {/* STATES */}
      {apiConditionMet === null && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
          justifyContent:"center", padding:"60px 0", gap:14, color:MUT }}>
          <FaSpinner style={{ fontSize:28, animation:"spin 1s linear infinite" }}/>
          <span style={{ fontSize:13, fontWeight:500 }}>Loading stock data…</span>
        </div>
      )}
      {apiConditionMet === false && (
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
          justifyContent:"center", padding:"52px 24px", gap:12, textAlign:"center" }}>
          <div style={{ width:64, height:64, borderRadius:"50%", background:"#ECFDF5",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:28 }}>✅</div>
          <div style={{ fontWeight:800, fontSize:17, color:"#111827" }}>पर्चा जमा हो चुका है</div>
          <div style={{ fontSize:13, color:MUT }}>Stock report for {displayDate} has already been submitted.</div>
        </div>
      )}

      {/* MAIN TABLE */}
      {apiConditionMet === true && (
        <div className="parcha-table-outer">
          <div className="parcha-table-card">
            <div className="parcha-table-scroll">
              <table className="parcha-table">
                <colgroup>
                  <col style={{width:32}}/>
                  <col style={{width:220}}/><col style={{width:180}}/><col style={{width:76}}/>
                  <col style={{width:66}}/><col style={{width:72}}/><col style={{width:80}}/>
                  <col style={{width:80}}/><col style={{width:70}}/><col style={{width:84}}/>
                  <col style={{width:88}}/><col style={{width:96}}/>
                </colgroup>
                <thead>
                  <tr>
                    <th className="th-cat"></th>
                    <th className="th-left sticky-1" style={{left:32}}>Product Name</th>
                    <th className="th-left sticky-2" style={{left:252}}>Variant (Bottle)</th>
                    <th className="th-left sticky-3" style={{left:432}}>Variant (PET)</th>
                    <th>Rate</th><th>Opening</th><th>Received</th><th>Transfer</th>
                    <th>Total</th><th className="th-center">Closing</th>
                    <th>Sale Value</th><th>Total Sale Value</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSegments.map((seg, si) => {
                    const cat = seg.cat;
                    const catRows = seg.rows;
                    const groups = buildGroups(catRows);
                    return (
                      <React.Fragment key={`${cat || "all"}-${si}`}>
                        {groups.map(({ name, variants }, gi) => {
                          const isCatFirst = gi === 0;
                          const selId      = selectedVars[name] ?? variants[0].id;
                          const r          = variants.find(v => v.id === selId) || variants[0];
                          const tot        = rowTotal(r);
                          const vl         = rowValue(r);
                          const filled     = r.closing !== "";
                          const filledVars = variants.filter(v => v.closing !== "").length;
                          const allVal     = variants.reduce((s,v) => s + rowValue(v), 0);
                          return (
                            <tr key={name} className={`parcha-product-row ${filled?"row-filled":gi%2!==0?"row-even":""}`}>
                              {isCatFirst && (
                                <td rowSpan={groups.length} className="parcha-cat-col">
                                  {cat && <span className="parcha-cat-col-inner">{cat}</span>}
                                </td>
                              )}
                              <td className="parcha-pname-cell sticky-1" style={{left:36}} title={name}>
                                <div className="parcha-pname">{name}</div>
                                {variants.length > 1 && (
                                  <div className="parcha-filled-count" style={{color:filledVars===variants.length?GRN:MUT}}>
                                    {filledVars}/{variants.length} filled
                                  </div>
                                )}
                              </td>
                              <td className="sticky-2" style={{left:252,padding:"7px 8px"}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                                  {variants.filter(v=>v.variantType?.toLowerCase()!=="pet").map((v,vi) => {
                                    const isActive = v.id===selId, isDone = v.closing!=="";
                                    return (
                                      <React.Fragment key={v.id}>
                                        {vi>0 && <span style={{color:"#D1D5DB",margin:"0 5px",fontSize:11,flexShrink:0}}>|</span>}
                                        <button onClick={()=>selectVariant(name,v.id)}
                                          onFocus={()=>selectVariant(name,v.id)}
                                          tabIndex={0}
                                          data-vid={v.id}
                                          onKeyDown={e=>{
                                            if(e.key==="Tab"&&!e.shiftKey){e.preventDefault();focusClosingInput(v.id);}
                                            else if(e.key==="Tab"&&e.shiftKey){e.preventDefault();navigatePills(v.id,-1);}
                                            else if(e.key==="Enter"){e.preventDefault();focusClosingInput(v.id);}
                                          }}
                                          className="parcha-var-pill"
                                          style={{
                                            padding:isActive?"2px 10px":"2px 4px",
                                            borderRadius:isActive?20:4,
                                            fontWeight:isActive?700:400,
                                            background:isActive?P:"transparent",
                                            color:isActive?"#fff":isDone?P:"#6B7280",
                                            borderColor:isActive?P:"transparent",
                                            flexShrink:0,
                                          }}>
                                          {v.packing}{isDone&&!isActive?" ✓":""}
                                        </button>
                                      </React.Fragment>
                                    );
                                  })}
                                  {variants.filter(v=>v.variantType?.toLowerCase()!=="pet").length===0 && (
                                    <span style={{color:"#9CA3AF",fontSize:12}}>—</span>
                                  )}
                                </div>
                              </td>
                              <td className="sticky-3" style={{left:432,textAlign:"center",padding:"7px 8px"}}>
                                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",justifyContent:"center"}}>
                                  {variants.filter(v=>v.variantType?.toLowerCase()==="pet").length > 0
                                    ? variants.filter(v=>v.variantType?.toLowerCase()==="pet").map((v,vi) => {
                                        const isActive = v.id===selId, isDone = v.closing!=="";
                                        return (
                                          <React.Fragment key={v.id}>
                                            {vi>0 && <span style={{color:"#D1D5DB",margin:"0 5px",fontSize:11,flexShrink:0}}>|</span>}
                                            <button onClick={()=>selectVariant(name,v.id)}
                                              onFocus={()=>selectVariant(name,v.id)}
                                              tabIndex={0}
                                              data-vid={v.id}
                                              onKeyDown={e=>{
                                                if(e.key==="Tab"){e.preventDefault();navigatePills(v.id,e.shiftKey?-1:1);}
                                                else if(e.key==="Enter"){e.preventDefault();focusClosingInput(v.id);}
                                              }}
                                              className="parcha-var-pill"
                                              style={{
                                                padding:isActive?"2px 10px":"2px 4px",
                                                borderRadius:isActive?20:4,
                                                fontWeight:isActive?700:400,
                                                background:isActive?"#10B981":"transparent",
                                                color:isActive?"#fff":isDone?"#10B981":"#6B7280",
                                                borderColor:isActive?"#10B981":"transparent",
                                                flexShrink:0,
                                              }}>
                                              {v.packing}{isDone&&!isActive?" ✓":""}
                                            </button>
                                          </React.Fragment>
                                        );
                                      })
                                    : <span style={{color:"#9CA3AF",fontSize:12}}>—</span>
                                  }
                                </div>
                              </td>
                              <td className="td-right" style={{fontWeight:500}}>{fmt(r.mrp)}</td>
                              <td className="td-right">{r.openingStock}</td>
                              <td className="td-right">
                                <button className="parcha-txn-btn" data-bs-toggle="modal" data-bs-target="#recModal"
                                  onClick={()=>fetchTransactionData(r.id,"received")}
                                  style={{background:r.receivedQty>0?"#ECFDF5":"#F9FAFB",color:r.receivedQty>0?"#065F46":MUT,borderColor:r.receivedQty>0?"#6EE7B7":BDR}}>
                                  {r.receivedQty>0?`⬇ ${r.receivedQty}`:"—"}
                                </button>
                              </td>
                              <td className="td-right">
                                <button className="parcha-txn-btn" data-bs-toggle="modal" data-bs-target="#trnModal"
                                  onClick={()=>fetchTransactionData(r.id,"transfer")}
                                  style={{background:r.transferQty>0?"#FFF7ED":"#F9FAFB",color:r.transferQty>0?"#C2410C":MUT,borderColor:r.transferQty>0?"#FED7AA":BDR}}>
                                  {r.transferQty>0?`⬆ ${r.transferQty}`:"—"}
                                </button>
                              </td>
                              <td className="td-right" style={{fontWeight:700}}>{tot}</td>
                              <td className="td-center" style={{padding:"5px 8px"}}>
                                <input type="number" min="0" value={r.closing}
                                  ref={el=>closingRefs.current[r.id]=el}
                                  onChange={e=>handleClosing(r.id,e.target.value)} placeholder={String(tot)}
                                  onKeyDown={e=>{
                                    if(e.key==="Enter"||(e.key==="Tab"&&!e.shiftKey)){
                                      e.preventDefault();
                                      navigatePills(r.id,1);
                                    } else if(e.key==="Tab"&&e.shiftKey){
                                      e.preventDefault();
                                      const pill=document.querySelector(`.parcha-var-pill[data-vid="${r.id}"]`);
                                      if(pill) pill.focus();
                                    }
                                  }}
                                  className={`parcha-closing-input ${filled?"inp-filled":""}`}/>
                              </td>
                              <td className="td-right" style={{fontWeight:vl>0?700:500,color:vl>0?"#111827":MUT}}>{fmt(vl)}</td>
                              <td className="td-right">
                                <span className="parcha-total-val" style={{background:allVal>0?P:"#F3F4F6",color:allVal>0?"#fff":MUT}}>
                                  {fmt(allVal)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP CARD VIEW */}
      {apiConditionMet === true && (
        <div className="parcha-dsk-cards">
          {filteredSegments.map((seg, si) => {
            const cat = seg.cat;
            const catRows = seg.rows;
            const groups = buildGroups(catRows);
            const showCat = cat !== "";
            return (
              <React.Fragment key={`dsk-${cat || "all"}-${si}`}>
                {showCat && (
                  <div className="pdsk-cat-hdr">
                    <span className="pdsk-cat-hdr-name">{catEmoji(cat)} {cat}</span>
                    <span className="pdsk-cat-hdr-count">{groups.length} products</span>
                  </div>
                )}
                {groups.map(({ name, variants }) => {
                  const bottleVars = variants.filter(v => v.variantType?.toLowerCase() !== "pet");
                  const petVars    = variants.filter(v => v.variantType?.toLowerCase() === "pet");
                  const allVal     = variants.reduce((s, v) => s + rowValue(v), 0);
                  const filledVars = variants.filter(v => v.closing !== "").length;

                  const renderDskTable = (vars) => (
                    <table className="pdsk-tbl">
                      <colgroup>
                        <col style={{width:"24%"}}/>
                        <col style={{width:"9%"}}/>
                        <col style={{width:"9%"}}/>
                        <col style={{width:"12%"}}/>
                        <col style={{width:"12%"}}/>
                        <col style={{width:"10%"}}/>
                        <col style={{width:"10%"}}/>
                        <col style={{width:"14%"}}/>
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="pdsk-th-left">Variant</th>
                          <th>Rate</th>
                          <th>Opening</th>
                          <th className="pdsk-th-recv">Receive</th>
                          <th className="pdsk-th-trnf">Transfer</th>
                          <th>Total</th>
                          <th>Closing</th>
                          <th>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vars.map(v => {
                          const tot  = rowTotal(v);
                          const vl   = rowValue(v);
                          const done = v.closing !== "";
                          return (
                            <tr key={v.id} className={done ? "pdsk-tr-done" : ""}>
                              <td className="pdsk-td-var">{v.packing}</td>
                              <td className="pdsk-td-c">{fmt(v.mrp)}</td>
                              <td className="pdsk-td-c">{v.openingStock}</td>
                              <td className="pdsk-td-recv">{v.receivedQty > 0 ? v.receivedQty : <span className="pdsk-dash">—</span>}</td>
                              <td className="pdsk-td-trnf">{v.transferQty > 0 ? v.transferQty : <span className="pdsk-dash">—</span>}</td>
                              <td className="pdsk-td-c" style={{fontWeight:700}}>{tot}</td>
                              <td className="pdsk-td-close">
                                <input
                                  type="number"
                                  min="0"
                                  value={v.closing}
                                  placeholder={String(tot)}
                                  ref={el => closingRefs.current[v.id] = el}
                                  onChange={e => handleClosing(v.id, e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
                                      e.preventDefault();
                                      const all = Array.from(document.querySelectorAll(".pdsk-close-inp"));
                                      const idx = all.indexOf(e.currentTarget);
                                      if (all[idx + 1]) { all[idx + 1].focus(); all[idx + 1].select(); }
                                    } else if (e.key === "Tab" && e.shiftKey) {
                                      e.preventDefault();
                                      const all = Array.from(document.querySelectorAll(".pdsk-close-inp"));
                                      const idx = all.indexOf(e.currentTarget);
                                      if (all[idx - 1]) { all[idx - 1].focus(); all[idx - 1].select(); }
                                    }
                                  }}
                                  className={`pdsk-close-inp${done ? " done" : ""}`}
                                />
                              </td>
                              <td className={`pdsk-td-val${vl > 0 ? " pos" : ""}`}>{fmt(vl)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );

                  return (
                    <div key={name} className="pdsk-card">
                      <div className="pdsk-card-hdr">
                        <div className="pdsk-card-name">{name}</div>
                        <div className="pdsk-card-badges">
                          {cat && <span className="pdsk-badge cat">{cat}</span>}
                          {variants.length > 1 && (
                            <span className="pdsk-badge vars">{variants.length} variants</span>
                          )}
                          <span className={`pdsk-badge filled${filledVars === variants.length ? " complete" : ""}`}>
                            {filledVars}/{variants.length} Filled
                          </span>
                        </div>
                      </div>
                      {bottleVars.length > 0 && (
                        <>
                          <div className="pdsk-section-lbl">Bottle</div>
                          {renderDskTable(bottleVars)}
                        </>
                      )}
                      {petVars.length > 0 && (
                        <>
                          <div className="pdsk-section-lbl pet">PET</div>
                          {renderDskTable(petVars)}
                        </>
                      )}
                      <div className="pdsk-card-footer">
                        <span>Total Sale Value (All Variants)</span>
                        <span className={`pdsk-footer-val${allVal > 0 ? " pos" : ""}`}>{fmt(allVal)}</span>
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* TOTAL SALE AMOUNT CARD */}
      {apiConditionMet === true && (
        <div className="parcha-sale-amt-card">
          <div className="parcha-sale-amt-label">Total Sale Amount</div>
          <div className="parcha-sale-amt-value">{fmt(totalValue)}</div>
        </div>
      )}

      {/* DAILY CLOSING SUMMARY */}
      {apiConditionMet === true && (
        <div className="parcha-closing-summary">
          <div className="pcs-title">Daily Closing Summary</div>
          <div className="pcs-body">
            <div className="pcs-row3">
              <div className="pcs-field">
                <div className="pcs-label">English Commission</div>
                <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                  <input type="number" className="pcs-inp" placeholder="0" value={commission} onChange={e=>setCommission(e.target.value)}/></div>
              </div>
              <div className="pcs-field">
                <div className="pcs-label">PhonePe Collection</div>
                <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                  <input type="number" className="pcs-inp" placeholder="0" value={phonePe} onChange={e=>setPhonePe(e.target.value)}/></div>
              </div>
              <div className="pcs-field">
                <div className="pcs-label">Net Cash In Hand</div>
                <div className="pcs-inp-wrap pcs-readonly"><span className="pcs-prefix">₹</span>
                  <span className="pcs-inp pcs-auto-val">{fmt(cashColl)}</span></div>
              </div>
            </div>
            <div className="pcs-row1">
              <div className="pcs-field">
                <div className="pcs-label">Additional Notes</div>
                <textarea className="pcs-textarea" placeholder="Any additional notes or remarks..."
                  value={addNotes} onChange={e=>setAddNotes(e.target.value)}/>
              </div>
            </div>
            <div className="pcs-totals" style={{gridTemplateColumns:"repeat(2,1fr)"}}>
              <div className="pcs-tot-box blue"><div className="pcs-tot-lbl">Online (PhonePe + Commission)</div><div className="pcs-tot-val">{fmt((parseFloat(phonePe)||0)+(parseFloat(commission)||0))}</div></div>
              <div className="pcs-tot-box green"><div className="pcs-tot-lbl">Net Cash In Hand</div><div className="pcs-tot-val">{fmt(cashColl)}</div></div>
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM ACTIONS (desktop only) */}
      {apiConditionMet === true && (
        <div className="parcha-bottom-actions">
          <button onClick={()=>setPreview(true)} disabled={filledCount===0} style={{
            display:"flex",alignItems:"center",gap:6,
            background:filledCount===0?"#F3F4F6":"#FDF4F8",color:filledCount===0?MUT:P,
            border:`1.5px solid ${filledCount===0?BDR:P+"44"}`,borderRadius:8,
            padding:"9px 20px",fontSize:13,fontWeight:700,
            cursor:filledCount===0?"not-allowed":"pointer",whiteSpace:"nowrap",
          }}>👁 Preview</button>
          <button onClick={handleSubmit} disabled={loading} style={{
            display:"flex",alignItems:"center",gap:6,
            background:loading?"#9CA3AF":`linear-gradient(135deg,${PDK},${P})`,
            color:"#fff",border:"none",borderRadius:8,padding:"9px 22px",fontSize:13,fontWeight:700,
            cursor:loading?"not-allowed":"pointer",boxShadow:`0 4px 12px ${P}44`,whiteSpace:"nowrap",
          }}>
            {loading?<><FaSpinner style={{animation:"spin 1s linear infinite"}}/> Submitting…</>:"✅ Submit Report"}
          </button>
        </div>
      )}

      {/* ═══ MOBILE CARD VIEW ════════════════════════ */}
      <div className="parcha-mobile-cards">
        {apiConditionMet === true && filteredSegments.map((seg, si) => {
          const cat = seg.cat;
          const catRows = seg.rows;
          const groups = buildGroups(catRows);
          const showCat = cat !== "";
          return (
            <React.Fragment key={`${cat||"all"}-${si}`}>
              {showCat && (
                <div className="m2cat-hdr">
                  <span className="m2cat-name">{catEmoji(cat)} {cat}</span>
                  <span className="m2cat-count">{groups.length} products</span>
                </div>
              )}
              {groups.map(({name,variants}) => {
                const bottleVars = variants.filter(v=>v.variantType?.toLowerCase()!=="pet");
                const petVars    = variants.filter(v=>v.variantType?.toLowerCase()==="pet");
                const allVal     = variants.reduce((s,v)=>s+rowValue(v),0);
                const filledVars = variants.filter(v=>v.closing!=="").length;
                const renderVarTable = (vars) => (
                  <table className="m2tbl">
                    <thead>
                      <tr>
                        <th className="m2th-l">VARIANT</th>
                        <th>RATE</th><th>OPEN</th>
                        <th className="m2th-recv">RECV</th>
                        <th className="m2th-trnf">TRNF</th>
                        <th>CLOSE</th><th className="m2th-r">VALUE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vars.map(v => {
                        const tot  = rowTotal(v);
                        const vl   = rowValue(v);
                        const done = v.closing !== "";
                        return (
                          <tr key={v.id} className={done?"m2tr-done":""}>
                            <td className="m2td-var">{v.packing}</td>
                            <td className="m2td-c">₹{v.mrp}</td>
                            <td className="m2td-c">{v.openingStock}</td>
                            <td className="m2td-recv">{v.receivedQty>0?v.receivedQty:<span className="m2dash">—</span>}</td>
                            <td className="m2td-trnf">{v.transferQty>0?v.transferQty:<span className="m2dash">—</span>}</td>
                            <td className="m2td-close">
                              <input type="text" inputMode="numeric" pattern="[0-9]*"
                                value={v.closing} placeholder={String(tot)}
                                onChange={e=>handleClosing(v.id,e.target.value)}
                                className={`m2inp${done?" done":""}`}/>
                            </td>
                            <td className={`m2td-val${vl>0?" pos":""}`}>{fmt(vl)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
                return (
                  <div key={name} className="m2card">
                    <div className="m2card-hdr">
                      <div className="m2card-name">{name}</div>
                      {variants.length>1 && (
                        <div className="m2card-filled" style={{color:filledVars===variants.length?"#16A34A":"#9CA3AF"}}>
                          {filledVars}/{variants.length}
                        </div>
                      )}
                    </div>
                    {bottleVars.length>0 && (
                      <>
                        <div className="m2sect">BOTTLE</div>
                        {renderVarTable(bottleVars)}
                      </>
                    )}
                    {petVars.length>0 && (
                      <>
                        <div className="m2sect pet">PET</div>
                        {renderVarTable(petVars)}
                      </>
                    )}
                    <div className="m2card-footer">
                      <span>TOTAL SALE VALUE (ALL VARIANTS)</span>
                      <span className={`m2footer-val${allVal>0?" pos":""}`}>{fmt(allVal)}</span>
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Mobile Total Sale Amount */}
        {apiConditionMet === true && (
          <div className="parcha-sale-amt-card">
            <div className="parcha-sale-amt-label">Total Sale Amount</div>
            <div className="parcha-sale-amt-value">{fmt(totalValue)}</div>
          </div>
        )}

        {/* Mobile Daily Closing Summary */}
        {apiConditionMet === true && (
          <div className="parcha-closing-summary">
            <div className="pcs-title">Daily Closing Summary</div>
            <div className="pcs-body">
              <div className="pcs-row3">
                <div className="pcs-field">
                  <div className="pcs-label">English Commission</div>
                  <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                    <input type="number" className="pcs-inp" placeholder="0" value={commission} onChange={e=>setCommission(e.target.value)}/></div>
                </div>
                <div className="pcs-field">
                  <div className="pcs-label">PhonePe Collection</div>
                  <div className="pcs-inp-wrap"><span className="pcs-prefix">₹</span>
                    <input type="number" className="pcs-inp" placeholder="0" value={phonePe} onChange={e=>setPhonePe(e.target.value)}/></div>
                </div>
                <div className="pcs-field">
                  <div className="pcs-label">Net Cash In Hand</div>
                  <div className="pcs-inp-wrap pcs-readonly"><span className="pcs-prefix">₹</span>
                    <span className="pcs-inp pcs-auto-val">{fmt(cashColl)}</span></div>
                </div>
              </div>
              <div className="pcs-totals" style={{gridTemplateColumns:"repeat(2,1fr)"}}>
                <div className="pcs-tot-box blue"><div className="pcs-tot-lbl">Online (PhonePe + Commission)</div><div className="pcs-tot-val">{fmt((parseFloat(phonePe)||0)+(parseFloat(commission)||0))}</div></div>
                <div className="pcs-tot-box green"><div className="pcs-tot-lbl">Net Cash In Hand</div><div className="pcs-tot-val">{fmt(cashColl)}</div></div>
              </div>
            </div>
          </div>
        )}

        <div className="mcards-action-bar">
          <button onClick={()=>setPreview(true)} disabled={filledCount===0} className="mcards-preview-btn">👁 Preview</button>
          <button onClick={handleSubmit} disabled={loading} className="mcards-submit-btn">
            {loading?<><FaSpinner style={{animation:"spin 1s linear infinite"}}/> Submitting…</>:"✈ Submit"}
          </button>
        </div>
      </div>

      {/* ═══ PREVIEW MODAL ════════════════════════════ */}
      {preview && (
        <div style={{ position:"fixed", inset:0, zIndex:9999,
          background:"rgba(15,23,42,0.6)", backdropFilter:"blur(4px)",
          display:"flex", alignItems:"flex-start", justifyContent:"center",
          padding:"24px 16px", overflowY:"auto" }}>
          <div style={{ background:"#fff", borderRadius:14, width:"100%", maxWidth:1100,
            boxShadow:"0 24px 64px rgba(0,0,0,0.25)" }}>

            <div style={{ padding:"16px 24px", borderBottom:`1px solid ${BDR}`,
              display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div>
                <div style={{ fontSize:16, fontWeight:800, color:P }}>👁 पर्चा Preview — अंग्रेजी</div>
                <div style={{ fontSize:12, color:MUT, marginTop:2 }}>
                  Date: {displayDate} &nbsp;|&nbsp; Filled: {filledCount} of {rows.length}
                </div>
              </div>
              <button onClick={() => setPreview(false)} style={{
                background:"#F3F4F6", border:`1px solid ${BDR}`, borderRadius:7,
                padding:"7px 16px", fontSize:12, fontWeight:600, cursor:"pointer", color:"#374151" }}>
                ✕ Close
              </button>
            </div>

            <div style={{ padding:"12px 24px 0", display:"flex", gap:12, flexWrap:"wrap" }}>
              {[
                { label:"Total Products", val: rows.length },
                { label:"Qty Sold",       val: totalSold },
                { label:"Sale Value",     val: fmt(totalValue) },
                { label:"Filled",         val: `${filledCount}/${rows.length}` },
              ].map(c => (
                <div key={c.label} style={{ flex:"1 1 120px", border:`1px solid ${BDR}`,
                  borderRadius:8, padding:"10px 14px", background:"#FAFAFA" }}>
                  <div style={{ fontSize:10, color:MUT, fontWeight:600, marginBottom:3 }}>{c.label}</div>
                  <div style={{ fontSize:17, fontWeight:800, color:"#111827" }}>{c.val}</div>
                </div>
              ))}
            </div>

            <div style={{ padding:"16px 24px" }}>
              <div style={{ border:`1px solid ${BDR}`, borderRadius:10, overflow:"hidden" }}>
                {(() => {
                  const pvSegments = buildCategorySegments(rows.filter(r => rowSold(r) > 0));
                  return pvSegments.map((seg, ci) => {
                    const cat = seg.cat;
                    const catRows = seg.rows;
                    const groups  = buildGroups(catRows);
                    const subVal  = catRows.reduce((s,r)=>s+rowValue(r),0);
                    const subSold = catRows.reduce((s,r)=>s+rowSold(r),0);
                    return (
                      <div key={`${cat||"all"}-${ci}`}
                        style={{ borderBottom:ci<pvSegments.length-1?`1px solid ${BDR}`:"none" }}>
                        {cat !== "" && (
                          <div style={{
                            display:"flex", alignItems:"center", gap:8,
                            padding:"6px 14px 6px 16px",
                            background:"linear-gradient(90deg,#F3E9F0,#FAF5F8 60%,transparent)",
                            borderTop:`1px solid ${BDR}`, borderBottom:`2px solid #D8A4C0`,
                          }}>
                            <span style={{display:"block",width:3,height:14,background:P,borderRadius:2,flexShrink:0}}/>
                            <span style={{fontSize:10,fontWeight:800,color:P,textTransform:"uppercase",letterSpacing:1.5}}>
                              {catEmoji(cat)} {cat}
                            </span>
                          </div>
                        )}
                        <div style={{ flex:1, overflowX:"auto" }}>
                            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12, minWidth:700 }}>
                              <thead>
                                <tr style={{ background:P }}>
                                  {["Product","Vol","Rate","Opening","Received","Transfer","Total","Closing","Sold","Value"].map(h => (
                                    <th key={h} style={{ padding:"7px 10px", color:"#fff", fontWeight:600, fontSize:10,
                                      textAlign:["Product","Vol"].includes(h)?"left":"right",
                                      borderRight:"1px solid rgba(255,255,255,0.10)", whiteSpace:"nowrap" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {groups.flatMap(({ name, variants }) =>
                                  variants.map((r, vi) => {
                                    const isFirst = vi === 0;
                                    const tot = rowTotal(r), sl = rowSold(r), vl = rowValue(r);
                                    const filled = r.closing !== "";
                                    const topBdr = isFirst ? "2px solid #D1D5DB" : `1px solid ${BDR}`;
                                    return (
                                      <tr key={r.id} style={{ background:filled?"#FDF9FB":"#fff" }}>
                                        {isFirst && (
                                          <td rowSpan={variants.length} style={{ padding:"8px 12px",
                                            fontWeight:700, color:"#111827", fontSize:12,
                                            borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                            borderTop:topBdr, verticalAlign:"middle",
                                            background:"#FEFBFD", minWidth:130 }}>{name}</td>
                                        )}
                                        <td style={{ padding:"7px 10px",
                                          borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                          borderTop:isFirst?topBdr:undefined }}>
                                          <span style={{ background:"#F3F4F6", borderRadius:4,
                                            padding:"1px 6px", fontSize:10, fontWeight:600, color:"#374151" }}>
                                            {r.packing}
                                          </span>
                                        </td>
                                        <SNC top={isFirst?topBdr:undefined}>{fmt(r.mrp)}</SNC>
                                        <SNC top={isFirst?topBdr:undefined}>{r.openingStock}</SNC>
                                        <SNC top={isFirst?topBdr:undefined} color={GRN} bold>{r.receivedQty}</SNC>
                                        <SNC top={isFirst?topBdr:undefined} color={ORG} bold>{r.transferQty}</SNC>
                                        <SNC top={isFirst?topBdr:undefined} bold>{tot}</SNC>
                                        <td style={{ padding:"7px 10px", textAlign:"center",
                                          fontWeight:700, color:filled?P:MUT,
                                          borderBottom:`1px solid ${BDR}`, borderRight:`1px solid ${BDR}`,
                                          borderTop:isFirst?topBdr:undefined }}>
                                          {filled ? r.closing : <span style={{color:"#D1D5DB"}}>—</span>}
                                        </td>
                                        <SNC top={isFirst?topBdr:undefined} color={sl>0?"#111827":MUT} bold={sl>0}>{sl}</SNC>
                                        <td style={{ padding:"7px 10px", textAlign:"right", fontWeight:600,
                                          color:vl>0?"#111827":MUT,
                                          borderBottom:`1px solid ${BDR}`, borderTop:isFirst?topBdr:undefined }}>
                                          {fmt(vl)}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                            {cat !== "" && (
                              <div style={{ display:"flex", justifyContent:"flex-end",
                                padding:"6px 14px", background:"#FDF4F8",
                                borderTop:`1px solid ${BDR}`, gap:16 }}>
                                <span style={{ fontSize:11, color:MUT }}>Sold: <b>{subSold}</b></span>
                                <span style={{ fontSize:11, fontWeight:700, color:MUT }}>{cat} Subtotal:</span>
                                <span style={{ fontSize:13, fontWeight:800, color:P, minWidth:70, textAlign:"right" }}>{fmt(subVal)}</span>
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  });
                })()}
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"14px 22px", background:"#FDF4F8", borderTop:`2px solid ${P}22` }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#111827" }}>Grand Total Sale Amount</div>
                    <div style={{ fontSize:11, color:MUT, marginTop:2 }}>Qty Sold: {totalSold} units</div>
                  </div>
                  <div style={{ fontSize:22, fontWeight:900, color:P }}>{fmt(totalValue)}</div>
                </div>
              </div>

              {uniqueCats.length > 0 && (
                <div style={{ marginTop:16, border:`1px solid ${BDR}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"9px 14px", background:P, fontSize:11, fontWeight:700, color:"#fff", letterSpacing:0.5 }}>
                    CATEGORY-WISE SUMMARY
                  </div>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ background:"#F8F8F8" }}>
                        {["Category","Variants","Qty Sold","Sale Value"].map(h => (
                          <th key={h} style={{ padding:"8px 14px", fontSize:11, fontWeight:700, color:MUT,
                            textAlign:["Category","Variants"].includes(h)?"left":"right",
                            borderBottom:`1px solid ${BDR}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uniqueCats.map((cat, i) => {
                        const cr   = rows.filter(r=>r.cat===cat && rowSold(r) > 0);
                        const cSld = cr.reduce((s,r)=>s+rowSold(r),0);
                        const cVal = cr.reduce((s,r)=>s+rowValue(r),0);
                        if (!cSld) return null;
                        return (
                          <tr key={cat} style={{ background:i%2===0?"#fff":"#FAFAFA" }}>
                            <td style={{ padding:"9px 14px", fontWeight:700, color:P, borderBottom:`1px solid ${BDR}` }}>{cat}</td>
                            <td style={{ padding:"9px 14px", color:"#374151", borderBottom:`1px solid ${BDR}` }}>{cr.length} variants sold</td>
                            <td style={{ padding:"9px 14px", textAlign:"right", fontWeight:600, color:"#111827", borderBottom:`1px solid ${BDR}` }}>{cSld}</td>
                            <td style={{ padding:"9px 14px", textAlign:"right", fontWeight:700, color:"#059669", borderBottom:`1px solid ${BDR}` }}>{fmt(cVal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background:"#FDF4F8" }}>
                        <td colSpan={2} style={{ padding:"10px 14px", fontWeight:800, color:"#111827" }}>Grand Total</td>
                        <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:800, color:"#111827" }}>{totalSold}</td>
                        <td style={{ padding:"10px 14px", textAlign:"right", fontWeight:900, color:P, fontSize:15 }}>{fmt(totalValue)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div style={{ padding:"14px 24px", borderTop:`1px solid ${BDR}`,
              display:"flex", justifyContent:"flex-end", gap:10,
              background:"#FAFAFA", borderRadius:"0 0 14px 14px" }}>
              <button onClick={() => setPreview(false)} style={{
                background:"#F3F4F6", color:"#374151", border:`1px solid ${BDR}`,
                borderRadius:8, padding:"9px 22px", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                Close
              </button>
              <button onClick={() => { setPreview(false); handleSubmit(); }} style={{
                background:`linear-gradient(135deg,${PDK},${P})`, color:"#fff", border:"none",
                borderRadius:8, padding:"9px 24px", fontSize:13, fontWeight:700, cursor:"pointer",
                boxShadow:`0 4px 12px ${P}44` }}>
                ✅ Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RECEIVED DETAIL MODAL */}
      <div className="modal fade" id="recModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ color:P, fontWeight:700 }}>⬇ Received Stock Details</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"/>
            </div>
            <div className="modal-body">
              {popupData1.length > 0 ? (
                <table className="table table-bordered table-striped">
                  <thead><tr><th>Product</th><th>Receive Type</th><th>Details</th><th>Qty</th></tr></thead>
                  <tbody>
                    {popupData1.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>{item.productName || "N/A"}</td>
                        <td>{item.receivedHead || "N/A"}</td>
                        <td>{item.receivedHead?.toUpperCase()==="RSBCL"?`Invoice: ${item.invoiceNo||"N/A"}`:
                          (item.receivedHead?.toUpperCase()?.includes("SHOP")||item.receivedHeadType?.toUpperCase()?.includes("SHOP"))?
                          `From: ${item.selectedShop||item.shopName||item.transferShopName||item.senderShopName||item.receivedHeadName||"N/A"}`:`Party: ${item.receivedHeadName||item.partyName||"N/A"}`}</td>
                        <td style={{ fontWeight:700 }}>{item.qty || "0"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyState message="No received stock data" />}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      {/* TRANSFER DETAIL MODAL */}
      <div className="modal fade" id="trnModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ color:P, fontWeight:700 }}>⬆ Transfer Stock Details</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"/>
            </div>
            <div className="modal-body">
              {popupData2.length > 0 ? (
                <table className="table table-bordered table-striped">
                  <thead><tr><th>Product</th><th>Transfer Head</th><th>Detail</th><th>Qty</th></tr></thead>
                  <tbody>
                    {popupData2.map((item, i) => (
                      <tr key={item.id || i}>
                        <td>{item.productName || "N/A"}</td>
                        <td>{item.transferHead || "N/A"}</td>
                        <td>{item.invoiceNo || item.transferPartyName || item.transferShopName || "N/A"}</td>
                        <td style={{ fontWeight:700 }}>{item.qty || "0"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <EmptyState message="No transfer data" />}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function NC({ children, color, bold }) {
  return (
    <td style={{ padding:"9px 12px", textAlign:"right",
      color:color||"#374151", fontWeight:bold?700:500,
      borderBottom:"1px solid #E5E7EB", borderRight:"1px solid #E5E7EB",
      verticalAlign:"middle" }}>{children}</td>
  );
}
function SNC({ children, top, color, bold }) {
  return (
    <td style={{ padding:"7px 10px", textAlign:"right",
      color:color||"#374151", fontWeight:bold?700:500,
      borderBottom:"1px solid #E5E7EB", borderRight:"1px solid #E5E7EB",
      borderTop:top }}>{children}</td>
  );
}
